'use client';

import React, { useId, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useGetAllTeams } from 'src/hooks/teams/useGetAllTeams';
import { Checkbox } from 'src/components/PulseUI';

interface StrategyTeamsDropdownProps {
  selectedTeamIds: string[];
  onTeamSelection: (teamIds: string[]) => void;
}

const StrategyTeamsDropdown: React.FC<StrategyTeamsDropdownProps> = ({
  selectedTeamIds,
  onTeamSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const labelId = useId();
  const listboxId = useId();

  // Get teams from hook
  const { data: teamsFromHook } = useGetAllTeams();

  const teams = teamsFromHook?.results || [];

  // Filter teams based on search
  const filteredTeams = teams.filter((team) =>
    team.name.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // Get selected team names for display
  const selectedTeamNames = teams
    .filter((team) => selectedTeamIds.includes(String(team.id)))
    .map((team) => team.name);

  const handleTeamToggle = (teamId: string, isSelected: boolean) => {
    if (isSelected) {
      onTeamSelection([...selectedTeamIds, teamId]);
    } else {
      onTeamSelection(selectedTeamIds.filter((id) => id !== teamId));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-1 relative"
      data-testid="strategy-teams-dropdown"
      onKeyDown={handleKeyDown}
    >
      <label
        id={labelId}
        className="text-primary text-sm font-semibold leading-tight"
      >
        Teams
      </label>

      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        data-testid="strategy-teams-dropdown-trigger"
        className="w-full px-4 py-2 border border-neutral-200 rounded-full outline-none bg-white hover:border-neutral-500 transition-all duration-200 text-left flex justify-between items-center min-h-[42px]"
      >
        <div className="flex-1 flex items-center gap-2 overflow-hidden">
          {selectedTeamIds.length === 0 ? (
            <span className="text-neutral-500">Select teams...</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {selectedTeamNames.slice(0, 2).map((teamName) => (
                <span
                  key={teamName}
                  className="inline-flex items-center px-2 py-1 bg-sky-100 text-sky-800 rounded-md text-xs font-medium"
                  data-testid="strategy-teams-dropdown-selected-badge"
                >
                  {teamName}
                </span>
              ))}
              {selectedTeamIds.length > 2 && (
                <span
                  className="inline-flex items-center px-2 py-1 bg-neutral-100 text-neutral-600 rounded-md text-xs font-medium"
                  data-testid="strategy-teams-dropdown-more-badge"
                >
                  +{selectedTeamIds.length - 2} more
                </span>
              )}
            </div>
          )}
        </div>
        <Icon
          icon={faCaretDown}
          className={`w-4 h-4 text-neutral-500 transition-all duration-300 ${
            isOpen ? '-rotate-90' : 'rotate-0'
          }`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          id={listboxId}
          role="listbox"
          aria-labelledby={labelId}
          aria-multiselectable="true"
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-xl shadow-lg z-50"
          data-testid="strategy-teams-dropdown-menu"
        >
          {/* Search Input */}
          <div className="p-2 border-b border-neutral-200">
            <input
              autoFocus
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search teams"
              data-testid="strategy-teams-dropdown-search"
              className="w-full px-3 py-2 border border-neutral-200 rounded-full text-sm outline-none"
            />
          </div>

          {/* Teams List */}
          <div
            className="max-h-48 overflow-y-auto"
            data-testid="strategy-teams-dropdown-list"
          >
            {filteredTeams.length === 0 ? (
              <div
                className="p-3 text-sm text-neutral-500"
                data-testid="strategy-teams-dropdown-empty"
              >
                No teams found
              </div>
            ) : (
              filteredTeams.map((team) => (
                <div
                  key={team.id}
                  role="option"
                  aria-selected={selectedTeamIds.includes(String(team.id))}
                  className="px-3 py-2 hover:bg-neutral-50 transition-colors"
                  data-testid={`strategy-teams-dropdown-option-${team.id}`}
                >
                  <Checkbox
                    checked={selectedTeamIds.includes(String(team.id))}
                    onChange={(e) =>
                      handleTeamToggle(String(team.id), e.target.checked)
                    }
                    label={team.name}
                    size="sm"
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
          data-testid="strategy-teams-dropdown-overlay"
        />
      )}
    </div>
  );
};

export default StrategyTeamsDropdown;
